import React, { useCallback, useEffect, useState } from 'react';
import { SxStyle } from '../../../../../types';
import { useStringsContext } from '../../../../../contexts/strings';
import { SquadPlayer } from '../../types';
import { Stats } from '../../api-types';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import PlayerTabContent from './player-tab-content';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import RemoveStatsModal from './remove-stats-modal';
import { useStats } from '../../hooks/stats';
import AddStatsModal from './add-edit-stats-modal';

const CURRENT_PROTECTED_LEAGUE_ID = 3839; // TODO Get from API when available

interface filteredStats
  extends Pick<
    Stats,
    | 'leagueName'
    | 'teamName'
    | 'gamesPlayed'
    | 'goals'
    | 'goalsConceded'
    | 'assists'
    | 'redCards'
    | 'yellowCards'
  > {
  seasonYears: string; // Combined seasonStartYear and seasonEndYear
}

const filteredStatsKeysList: (keyof filteredStats)[] = [
  'seasonYears',
  'leagueName',
  'teamName',
  'gamesPlayed',
  'goals',
  'goalsConceded',
  'assists',
  'redCards',
  'yellowCards',
];

interface statsRowsType {
  key: keyof filteredStats;
  value: filteredStats[keyof filteredStats];
}

const styles: SxStyle = {
  tableRow: (theme) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    height: '53px',
  }),
  tableCell: {
    '&:first-child': {
      minWidth: 120,
    },
  },
};

interface EditButtonsProps {
  onEdit: () => void;
  onDelete: () => void;
}

const ListEditButtons = ({ onEdit, onDelete }: EditButtonsProps) => (
  <Stack spacing={1} direction="row" alignItems="center">
    <IconButton onClick={onEdit}>
      <EditIcon color="primary" />
    </IconButton>
    <IconButton onClick={onDelete}>
      <DeleteIcon color="error" />
    </IconButton>
  </Stack>
);

interface Props {
  player: SquadPlayer;
  season: number;
}
const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;
const CURRENT_YEAR = parseInt(REACT_APP_DEFAULT_LEAGUE_SEASON as string, 10);

const PlayerTabStats = ({ player, season }: Props) => {
  const { strings } = useStringsContext();
  const { fetchStats } = useStats();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<statsRowsType[][] | null>(null);
  const [showAddStats, setShowAddStats] = useState(false);
  const [showEditStats, setShowEditStats] = useState(false);
  const [showDeleteStats, setShowDeleteStats] = useState(false);
  const [playerStats, setPlayerStats] = useState<Stats[] | null>(null);
  const [selectedPlayerStats, setSelectedPlayerStats] = useState<Stats | null>(null);

  const onUpdateStats = useCallback(
    async (player: SquadPlayer) => {
      setLoading(true);
      try {
        const playerStats = await fetchStats(player);

        if (playerStats) {
          playerStats.reverse();
          const statsRows = playerStats.map((stats) =>
            filteredStatsKeysList.map((key) => ({
              key: key,
              value:
                key === 'seasonYears'
                  ? `${stats.seasonStartYear} - ${stats.seasonEndYear}`
                  : stats[key],
            }))
          );
          setRows(statsRows);
          setPlayerStats(playerStats);
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchStats]
  );

  useEffect(() => {
    const getStats = async () => {
      if (player) {
        onUpdateStats(player);
      }
    };
    getStats();
  }, [fetchStats, onUpdateStats, player]);

  const onAddStats = () => {
    const newStats: Stats = {
      id: '',
      goals: 0,
      assists: 0,
      redCards: 0,
      yellowCards: 0,
      gamesPlayed: 0,
      goalsConceded: 0,
      leagueName: '',
      teamName: '',
      seasonStartYear: 0,
      seasonEndYear: 0,
    };
    setSelectedPlayerStats(newStats);
    setShowAddStats(true);
  };

  const onEditStats = (selectedStats: Stats) => {
    setSelectedPlayerStats(selectedStats);
    setShowEditStats(true);
  };

  const onDeleteStats = (selectedStats: Stats) => {
    setSelectedPlayerStats(selectedStats);
    setShowDeleteStats(true);
  };

  return (
    <>
      <PlayerTabContent
        title={strings.squadPlayerTabs.stats}
        loading={loading}
        buttonElement={
          <Button
            variant="contained"
            onClick={onAddStats}
            startIcon={<AddIcon />}
            disabled={season !== CURRENT_YEAR}
          >
            {strings.squad.add}
          </Button>
        }
      >
        <>
          <TableHead>
            <TableRow>
              {filteredStatsKeysList.map((key, index) => (
                <TableCell sx={styles.tableCell} key={index} align={index < 3 ? 'left' : 'center'}>
                  {strings.squadPlayerStats[key]}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((stats, index) => (
              <TableRow key={index} sx={styles.tableRow}>
                {stats.map((stat: statsRowsType, index) => (
                  <TableCell key={index} size="small" align={index < 3 ? 'left' : 'center'}>
                    {stat.value}
                  </TableCell>
                ))}
                <TableCell size="small">
                  {playerStats && playerStats[index]?.smcId !== CURRENT_PROTECTED_LEAGUE_ID && (
                    <ListEditButtons
                      onEdit={() => onEditStats(playerStats![index])}
                      onDelete={() => onDeleteStats(playerStats![index])}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </>
      </PlayerTabContent>
      {showAddStats && (
        <AddStatsModal
          open={showAddStats}
          onClose={() => setShowAddStats(false)}
          onChangedStats={onUpdateStats}
          stats={selectedPlayerStats!}
          player={player}
        />
      )}
      {showEditStats && (
        <AddStatsModal
          open={showEditStats}
          onClose={() => setShowEditStats(false)}
          onChangedStats={onUpdateStats}
          stats={selectedPlayerStats!}
          player={player}
          isEdit
        />
      )}
      {showDeleteStats && (
        <RemoveStatsModal
          open={showDeleteStats}
          onClose={() => setShowDeleteStats(false)}
          stats={selectedPlayerStats!}
          player={player}
          onRemoveStats={onUpdateStats}
        />
      )}
    </>
  );
};

export default PlayerTabStats;
